import { dappStore } from '@components/store';
import { useContracts, useProvider, useActivePositionId, useCurrentError } from '@components/store/getters';
import { useMemo, useState } from 'react';
import { naturalNumber } from '@components/eth/Utils';
import moment from 'moment';
import { Position } from 'types';
import { createOpenseaURL } from 'utils/createOpenseaURL';
import { useWeb3Methods } from 'hooks/useWeb3Methods';
import Link from '@components/common/Link';
import mixpanel from 'mixpanel-browser';
import { TableWrapper, ActionsWrapper, StyledErrorBox, StyledDescriptionText } from './ClosePositionModal.styles';
import Button from '@components/common/Button';
import { toBigNumber } from 'utils/toBigNumber';
import { parseError } from 'utils/parseError';
import { roundNumber } from 'utils/roundNumber';

interface ClosePositionModalProps {
  activePosition: Position;
}

export default function ClosePositionModal({ activePosition }: ClosePositionModalProps) {
  const close = () => {
    dappStore.setState({ isClosePositionModal: false });
  };
  const [pending, setPending] = useState(false);
  const provider = useProvider();
  const nftID = useActivePositionId();
  const contracts = useContracts();
  const currentError = useCurrentError();
  const { updateUserBalances, updateApprovalStatus } = useWeb3Methods();

  const closePosition = async () => {
    setPending(true);
    // console.log('Closing position ' + nftID);
    try {
      const collateralBigNumber = toBigNumber(Number(activePosition.collateral), 18);
      const minimumOusdReturned = collateralBigNumber.mul(90).div(100);
      const tx = await contracts.levEngine.unwindLeveragedPosition(nftID, minimumOusdReturned);
      dappStore.setState({ isClosePositionModal: false });
      dappStore.setState({ isClosingPositionModal: true });
      provider.once(tx.hash, async (transaction) => {
        // Emitted when the transaction has been mined
        // We dont want to show the processing modal for 1 second and then close it
        // It can be too jaring
        // So we artificially delay /set a minimum show time of 2 seconds
        // console.log('Transaction mined! ', transaction);
        await updateUserBalances();
        await updateApprovalStatus();
        mixpanel.track('PositionClosed', {
          nftid: nftID,
        });
        setTimeout(() => {
          setPending(false);
          dappStore.setState({ isClosingPositionModal: false });
          dappStore.setState({ isCloseConfirmationModal: true });
          dappStore.setState({ activePositionId: null });
        }, 2000);
      });
    } catch (error: any) {
      console.error(error);
      const message: string = error?.error?.data?.message;
      if (error?.reason?.includes('rejected transaction')) {
        dappStore.setState({ currentError: { message: 'Transaction rejected by user.' } });
      } else {
        dappStore.setState({ currentError: parseError(error) });
      }
      if (
        message?.toLowerCase().includes('Not enough OUSD for exchange') ||
        message?.toLowerCase().includes('3/lv insuf eX to lvUSD')
      ) {
        dappStore.setState({
          showUnavailableClosingPositionModal: true,
          isClosePositionModal: false,
          activePositionId: null,
        });
      }
      setPending(false);
    }
  };

  const expiryUnix = useMemo(() => {
    if (activePosition) return activePosition.unixTimestampOpened + 370 * 86400;
  }, [activePosition]);

  const expiryDate = useMemo(() => {
    if (expiryUnix) return moment.unix(expiryUnix).format('MMM DD, YYYY');
  }, [expiryUnix]);

  const minimumOusdReturned = useMemo(() => {
    if (activePosition?.collateral) {
      const collateral = Number(activePosition?.collateral);
      return roundNumber(collateral * 0.9, 2);
    }
  }, [activePosition?.collateral]);

  return (
    <>
      <StyledDescriptionText removeMargin={currentError}>
        The total value of this position (collateral + earnings) that you will receive is expected to be a minimum of{' '}
        <strong>{minimumOusdReturned} OUSD.</strong>
      </StyledDescriptionText>
      <StyledErrorBox />
      <TableWrapper>
        <table>
          <thead>
            <tr>
              <th>Opened</th>
              {/* <th>Net APY</th> */}
              <th>Collateral</th>
              <th>Borrowed</th>
              <th>Earnings</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{moment.unix(activePosition.unixTimestampOpened).format('MMM DD, YYYY')}</td>
              {/* <td>{naturalNumber(activePosition.apy.toString())}%</td> */}
              <td>{naturalNumber(activePosition.collateral)} OUSD</td>
              <td>{naturalNumber(activePosition.ousdBorrowed)} OUSD</td>
              <td>
                {parseFloat(naturalNumber(activePosition.totalInterestEarned)) > 0
                  ? naturalNumber(activePosition.totalInterestEarned) + ' OUSD'
                  : '--'}
              </td>
              <td>{expiryDate}</td>
            </tr>
          </tbody>
        </table>
      </TableWrapper>
      <ActionsWrapper>
        {pending && (
          <Button variant="danger" disabled>
            Closing...
          </Button>
        )}
        {!pending && (
          <Button variant="danger" onClick={closePosition} disabled={currentError}>
            Close Position
          </Button>
        )}
        <Link variant="outline" href={createOpenseaURL(nftID)} target="_blank" rel="noreferrer">
          Sell on OpenSea
        </Link>
        <Button variant="outline" onClick={close}>
          Nevermind
        </Button>
      </ActionsWrapper>
    </>
  );
}
